import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CalendarDays, Trophy, Users, Clock, Mail, Lock, ArrowRight, User, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1600&q=80";

const FeatureCard = ({ icon: Icon, title, description }) => (
  <Card data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, '-')}`} className="p-8 hover:-translate-y-1 transition-transform duration-200 border-slate-200 bg-white">
    <Icon className="h-10 w-10 text-green-700 mb-4" strokeWidth={1.5} />
    <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-2 text-slate-900">{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
  </Card>
);

const TennisBall = ({ animate }) => (
  <svg className={`h-5 w-5 shrink-0 ${animate ? "animate-spin" : ""}`} viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="48" fill="#c8e414" stroke="#a8c200" strokeWidth="3"/>
    <path d="M15 50c0-12 8-23 20-28" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
    <path d="M65 78c12-5 20-16 20-28" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
    <path d="M85 50c0 12-8 23-20 28" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.5"/>
    <path d="M35 22c-12 5-20 16-20 28" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

export default function LandingPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loginMode, setLoginMode] = useState("social");
  const [emailView, setEmailView] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [clickedBtn, setClickedBtn] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("fb_error")) {
      toast.error("Accesso con Facebook non riuscito. Riprova.");
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const handleLogin = (provider) => {
    setClickedBtn(provider);
    setTimeout(() => {
      if (provider === "facebook") {
        window.location.href = `${API}/auth/facebook/login`;
      } else {
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      }
    }, 600);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Accesso effettuato!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = typeof err?.message === "string" ? err.message : "Errore di accesso";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    setSubmitting(true);
    try {
      const data = await api.post("/auth/register", { email, password, name, origin_url: window.location.origin });
      toast.success(data.message || "Registrazione completata! Controlla la tua email.");
      setEmailView("login");
      setPassword("");
      setConfirmPassword("");
      setName("");
    } catch (err) {
      toast.error(err.message || "Errore nella registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      toast.success(data.message || "Controlla la tua email per il link di reset.");
      setEmailView("login");
    } catch (err) {
      toast.error(err.message || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  const renderEmailForm = () => {
    if (emailView === "register") {
      return (
        <form onSubmit={handleRegister} className="space-y-3" data-testid="register-form">
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input data-testid="register-name-input" type="text" placeholder="Nome completo" value={name} onChange={(e) => setName(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input data-testid="register-email-input" type="email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input data-testid="register-password-input" type="password" placeholder="Password (min 6 caratteri)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="register-confirm-password-input" type="password" placeholder="Ripeti password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
          </div>
          <Button data-testid="register-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase tracking-wide rounded-sm py-5 gap-2 transition-all hover:shadow-lg hover:shadow-[#c8e414]/20">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <TennisBall />}
            Registrati
          </Button>
          <p className="text-center text-xs text-white/40 mt-1">
            Hai gia un account?{" "}
            <button type="button" onClick={() => setEmailView("login")} className="text-[#c8e414] hover:underline font-semibold">Accedi</button>
          </p>
        </form>
      );
    }

    if (emailView === "forgot") {
      return (
        <form onSubmit={handleForgotPassword} className="space-y-3" data-testid="forgot-form">
          <p className="text-white/60 text-xs text-center mb-2">Inserisci la tua email per ricevere il link di reset</p>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input data-testid="forgot-email-input" type="email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
          </div>
          <Button data-testid="forgot-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase tracking-wide rounded-sm py-5 gap-2 transition-all hover:shadow-lg hover:shadow-[#c8e414]/20">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Invia Link Reset
          </Button>
          <p className="text-center text-xs text-white/40 mt-1">
            <button type="button" onClick={() => setEmailView("login")} className="text-[#c8e414] hover:underline font-semibold">Torna al login</button>
          </p>
        </form>
      );
    }

    // Default: login
    return (
      <form onSubmit={handleEmailLogin} className="space-y-3" data-testid="login-form">
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-white/40" />
          <Input data-testid="login-email-input" type="email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40" />
          <Input data-testid="login-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414] focus:ring-[#c8e414]/20" />
        </div>
        <Button data-testid="login-email-btn" type="submit" disabled={submitting} className="w-full bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase tracking-wide rounded-sm py-5 gap-2 transition-all hover:shadow-lg hover:shadow-[#c8e414]/20">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <TennisBall />}
          Accedi
        </Button>
        <div className="flex justify-between text-xs text-white/40 mt-1">
          <button type="button" onClick={() => setEmailView("register")} className="text-[#c8e414] hover:underline font-semibold">Registrati gratis</button>
          <button type="button" onClick={() => setEmailView("forgot")} className="text-white/50 hover:underline">Password dimenticata?</button>
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 bg-[#c8e414] rounded-full flex items-center justify-center shadow-lg shadow-[#c8e414]/30">
                  <Trophy className="h-6 w-6 text-slate-900" strokeWidth={2.5} />
                </div>
                <span className="text-[#c8e414] text-xs font-bold uppercase tracking-[0.25em]">Tennis Tournament Manager</span>
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none">
                Tabellone<br />
                <span className="text-[#c8e414]">Facile</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-300 max-w-lg leading-relaxed">
                La piattaforma intelligente per organizzare il calendario delle partite,
                rispettando le preferenze di ogni giocatore e la disponibilita dei campi.
              </p>
              <p className="mt-3 text-sm text-[#c8e414]/80 font-medium">
                Registrazione gratuita &middot; 14 giorni di prova &middot; 1 torneo incluso
              </p>
            </div>

            <div className="w-full max-w-sm">
              <Card className="bg-white/[0.07] backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <p className="text-white font-heading text-lg font-bold uppercase tracking-wide text-center">Accedi</p>

                  <div className="flex bg-white/10 rounded-sm p-0.5">
                    <button data-testid="mode-social" onClick={() => setLoginMode("social")} className={`flex-1 text-xs font-bold uppercase tracking-wide py-2 rounded-sm transition-all ${loginMode === "social" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"}`}>
                      Social Login
                    </button>
                    <button data-testid="mode-email" onClick={() => { setLoginMode("email"); setEmailView("login"); }} className={`flex-1 text-xs font-bold uppercase tracking-wide py-2 rounded-sm transition-all ${loginMode === "email" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"}`}>
                      Email
                    </button>
                  </div>

                  {loginMode === "social" ? (
                    <div className="space-y-2.5">
                      <button data-testid="login-google-btn" onClick={() => handleLogin("google")} className="w-full flex items-center gap-3 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-sm px-4 py-3 rounded-sm transition-all hover:shadow-lg hover:shadow-white/10 hover:-translate-y-0.5 active:translate-y-0 group">
                        <TennisBall animate={clickedBtn === "google"} />
                        <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        <span className="flex-1 text-left">Accedi con Google</span>
                        <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </button>
                      <button data-testid="login-facebook-btn" onClick={() => handleLogin("facebook")} className="w-full flex items-center gap-3 bg-[#1877F2] hover:bg-[#166ae0] text-white font-semibold text-sm px-4 py-3 rounded-sm transition-all hover:shadow-lg hover:shadow-[#1877F2]/20 hover:-translate-y-0.5 active:translate-y-0 group">
                        <TennisBall animate={clickedBtn === "facebook"} />
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        <span className="flex-1 text-left">Accedi con Facebook</span>
                        <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </button>
                    </div>
                  ) : renderEmailForm()}

                  <div className="pt-2 border-t border-white/5">
                    <p className="text-[10px] text-white/30 text-center leading-relaxed">
                      14 giorni gratis &middot; Nessuna carta richiesta
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-3">Funzionalita</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-12">
            Tutto quello che ti serve
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard icon={CalendarDays} title="Calendario Automatico" description="Genera il calendario delle partite rispettando le preferenze orarie di ogni giocatore e la disponibilita dei campi." />
            <FeatureCard icon={Trophy} title="Tabellone Interattivo" description="Visualizza il tabellone del torneo per ogni categoria e inserisci i risultati in tempo reale." />
            <FeatureCard icon={Users} title="Gestione Giocatori" description="Registra fino a 256 giocatori per categoria con preferenze orarie personalizzate." />
            <FeatureCard icon={Clock} title="Durata per Categoria" description="Imposta la durata delle partite per ogni categoria: under 10, under 12, open e molte altre." />
          </div>
        </div>
      </div>

      <footer className="bg-slate-900 py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-[#c8e414] rounded-full flex items-center justify-center">
                <Trophy className="h-4 w-4 text-slate-900" strokeWidth={2.5} />
              </div>
              <p className="font-heading text-xl font-bold text-white uppercase tracking-tight">Tabellone Facile</p>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-slate-400 hover:text-white text-xs uppercase tracking-wide font-medium transition-colors flex items-center gap-1.5">
                <Shield className="h-3 w-3" /> Privacy Policy
              </Link>
              <Link to="/cookie-policy" data-testid="footer-cookie-link" className="text-slate-400 hover:text-white text-xs uppercase tracking-wide font-medium transition-colors">
                Cookie Policy
              </Link>
              <Link to="/terms-of-service" data-testid="footer-terms-link" className="text-slate-400 hover:text-white text-xs uppercase tracking-wide font-medium transition-colors">
                Condizioni d'Uso
              </Link>
              <Link to="/guida" data-testid="footer-guida-link" className="text-slate-400 hover:text-white text-xs uppercase tracking-wide font-medium transition-colors">
                Guida all'uso
              </Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-slate-500 text-xs">Gestione intelligente dei tornei di tennis</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
